"use client";

import React, { FC, PropsWithChildren, useState } from "react";
import { useToken } from "./AuthContext";
import { useAppLoadingContext } from "./AppLoadingContext";
import { DisplayItem, SortCategoryId } from "../../src/types";

import { useTogglesLogic } from "../hooks/useTogglesLogic";
import { useAllMarketCategoryRanking } from "../hooks/useAllMarketCategoryRanking";
import { useAllSortedItems } from "../hooks/useAllSortedItems";
import { useSortContext } from "./SortContext";
import { getNumberFromString } from "../utils";
import { useSortCategory } from "../hooks/useSortCategory";
import { MarketData } from "../types/MarketData";

interface IOwnProps {
  onBackPress?: () => void;
  onInstrumentClick(displayItem: DisplayItem): void;
}

interface IFilters {
  [key: string]: {
    min: number;
    max: number;
    checked: boolean;
    hidesNa: boolean;
    value: string[];
  };
}

export type ExtendedDisplayItem = Omit<DisplayItem, "price"> & MarketData;

const DiscoveryContext =
  React.createContext<
    | (ReturnType<typeof useDiscoveryContextValue> &
        Pick<IOwnProps, "onBackPress" | "onInstrumentClick">)
    | null
  >(null);

export const globalFilterFunction = (
  item: ExtendedDisplayItem,
  key: string,
  value: IFilters[string],
) => {
  // @ts-ignore
  const itemValue = item[key] || "";
  const multiplier = itemValue.includes("%") ? 100 : 1;
  const isNa = itemValue === "N/A" || !itemValue;
  if (value.hidesNa && isNa) {
    return false;
  }
  const itemNumber = getNumberFromString(itemValue);
  const minValue = value.min * multiplier || -Infinity;
  const maxValue = value.max * multiplier || Infinity;

  return itemNumber >= minValue && itemNumber <= maxValue;
};

export const DiscoveryContextProvider: FC<PropsWithChildren<IOwnProps>> = ({
  children,
  onBackPress,
  onInstrumentClick,
}) => {
  const value = useDiscoveryContextValue();
  return (
    <DiscoveryContext.Provider
      value={{
        ...value,
        onBackPress,
        onInstrumentClick,
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
};

const useDiscoveryContextValue = () => {
  const { environment } = useToken();
  const { compare, toggles, selectAll, clearToggles, ...rest } =
    useTogglesLogic();
  const { watchlistId } = useSortContext();
  const { data: sortCategories } = useSortCategory(watchlistId, environment);

  const { data: sortedItems } = useAllSortedItems({
    environment,
    watchlistId,
    sortCategories: sortCategories || [],
  });
  const [filters, setFilters] = useState<IFilters>({});
  const { data, isLoading, findCategoryIdByField } =
    useAllMarketCategoryRanking({
      watchlistId,
      environment,
      sortCategories: sortCategories || [],
    });

  const getFilteredDataString = (
    filter: (item: ExtendedDisplayItem) => boolean,
  ) => {
    return `${
      [...(data as ExtendedDisplayItem[])]?.filter(filter).length
    } items match your filters`;
  };
  const resetFilters = () => {
    setFilters({});
  };

  const allSectors = React.useMemo(() => {
    const sectors = new Set<string>();
    data?.forEach((item) => {
      sectors.add(item.Sector);
    });
    return Array.from(sectors);
  }, [data]);

  const getMinMaxString = (field: keyof MarketData) => {
    const filteredData = (data as ExtendedDisplayItem[])?.filter(
      (item) =>
        item[field] !== undefined &&
        item[field] !== null &&
        item[field] !== "N/A",
    );

    const max = filteredData?.sort((a, b) => {
      const aVal = String(a[field]);
      const bVal = String(b[field]);
      return getNumberFromString(bVal) - getNumberFromString(aVal);
    })?.[0]?.[field];

    const min = filteredData?.sort((a, b) => {
      const aVal = String(a[field]);
      const bVal = String(b[field]);
      return getNumberFromString(aVal) - getNumberFromString(bVal);
    })?.[0]?.[field];
    return `Existing values between ${min} and ${max}`;
  };

  const getSign = (field: keyof MarketData) => {
    const itemToTest = (data as ExtendedDisplayItem[]).find(
      (item) =>
        item[field] !== undefined &&
        item[field] !== null &&
        item[field] !== "N/A",
    );
    const value = itemToTest?.[field] || "";
    if (value.includes("%")) {
      return "%";
    }
    const lastChar = value.charAt(value.length - 1);
    return isNaN(Number(lastChar)) ? lastChar : "";
  };

  const { loading } = useAppLoadingContext();
  const getFilteredData = () => {
    const fromFiltersToBooleans = Object.entries(filters).map(
      ([key, value]) => {
        if (
          (!value?.checked ||
            (!value.max && value.max !== 0) ||
            (!value.min && value.min !== 0)) &&
          !value?.value
        )
          return () => true;
        if ((value.value?.length || 0) > 0) {
          return (item: ExtendedDisplayItem) => {
            return (
              // @ts-ignore
              value.value?.includes(item[key])
            );
          };
        }
        return (item: ExtendedDisplayItem) => {
          return globalFilterFunction(item, key, value);
        };
      },
    );
    return (item: ExtendedDisplayItem) =>
      fromFiltersToBooleans.every((f) => f(item));
  };

  const getItemNameFromId = (instrumentId: string) => {
    return data?.find((item) => item.instrumentId === instrumentId)?.name;
  };
  const filterComparedData = (data: any[]) => {
    if (compare) {
      return data?.filter((item) => toggles.has(item.instrumentId));
    }
    return data?.filter(getFilteredData());
  };

  const selectAllToggles = () => {
    selectAll(filterComparedData(data!).map((item) => item.instrumentId));
  };

  const isAllSelected = () => {
    return filterComparedData(data!)?.length === toggles!.size;
  };

  const deselectAllToggles = () => {
    clearToggles();
  };

  return {
    loading: loading || isLoading,
    data,
    sortedItems,
    watchlistId,
    filterComparedData,
    togglesArray: Array.from(toggles),
    compare,
    findCategoryIdByField,
    filters,
    setFilters,
    resetFilters,
    getFilteredDataString,
    getMinMaxString,
    allSectors,
    getSign,
    getItemNameFromId,
    clearToggles,
    selectAllToggles,
    isAllSelected,
    deselectAllToggles,
    ...rest,
  };
};

export const useDiscoveryContext = () => {
  const value = React.useContext(DiscoveryContext);
  return value;
};
